import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { useAuthStore } from '../store/authStore'
import { phase2API } from '../services/api'
import type { Phase2Group, Phase2Image, Phase2RatingItem, Phase2Status } from '../types'

type ScoreType = 'preference'

interface ImageScoreState {
  preference_score: number | null
}

interface GroupLocalState {
  expanded: boolean
  submitted: boolean
  submitting: boolean
  scores: Record<number, ImageScoreState>
}

const isUnavailablePhase2Image = (
  image: Phase2Image
): boolean => {
  if (!image.image_url?.trim()) {
    return true
  }

  const typeName = (image.type_name || '')
    .trim()
    .toLowerCase()

  return [
    'failed',
    'failure',
    'error',
    'uncertain',
    'timeout',
  ].some(token => typeName.includes(token))
}

export default function PhaseTwoPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const editMode = location.pathname === '/phase2/rescore' || searchParams.get('edit') === '1'
  const { user, isAuthenticated } = useAuthStore()

  const [activeBatch, setActiveBatch] = useState(0)
  const [totalBatches, setTotalBatches] = useState(1)
  const [status, setStatus] = useState<Phase2Status['status']>('pending')
  const [job, setJob] = useState<Phase2Status['job'] | null>(null)
  const [groups, setGroups] = useState<Phase2Group[]>([])
  const [groupStates, setGroupStates] = useState<Record<string, GroupLocalState>>({})
  const [loadedImageIds, setLoadedImageIds] = useState<Set<number>>(new Set())
  const [failedImageIds, setFailedImageIds] = useState<Set<number>>(new Set())
  const [previewImage, setPreviewImage] = useState<Phase2Image | null>(null)
  const [isStarting, setIsStarting] = useState(false)
  const [loading, setLoading] = useState(true)

  const loadGroups = useCallback(async (batch: number, allowRescore = false) => {
    const rawList = await phase2API.getGroups(batch)

    const list = rawList
      .map(group => ({
        ...group,
        images: group.images.filter(
          image => !isUnavailablePhase2Image(image)
        ),
      }))
      .filter(group =>
        group.images.some(image => !image.is_base_image)
      )

    setGroups(list)
    const initial: Record<string, GroupLocalState> = {}
    list.forEach((group, index) => {
      const scores: Record<number, ImageScoreState> = {}
      group.images.forEach((img) => {
        scores[img.id] = {
          preference_score: img.existing_preference_score ?? null,
        }
      })
      initial[group.group_id] = {
        expanded: index === 0,
        submitted: Boolean(group.submitted) && !allowRescore,
        submitting: false,
        scores,
      }
    })
    setGroupStates(initial)
    return list
  }, [])

  const refreshStatus = useCallback(async () => {
    const st = await phase2API.getStatus()
    setStatus(st.status)
    setTotalBatches(Math.max(1, st.total_batches ?? 1))
    setJob(st.job ?? null)
    return st
  }, [])

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/register')
      return
    }

    let cancelled = false
    let pollTimer: number | undefined

    const loadFirstIncompleteBatch = async (st: Phase2Status) => {
      const readyBatches = st.ready_batches ?? []
      for (const batch of readyBatches) {
        const loaded = await loadGroups(batch, editMode)
        const hasUnsubmittedGroup = loaded.some(group => !group.submitted)
        if (hasUnsubmittedGroup || batch === readyBatches[readyBatches.length - 1]) {
          if (!cancelled) setActiveBatch(batch)
          return true
        }
      }
      return false
    }

    const pollFirstBatch = async () => {
      if (cancelled) return
      try {
        const latest = await refreshStatus()
        if (await loadFirstIncompleteBatch(latest)) return
      } catch (error) {
        console.error('轮询第二阶段状态失败:', error)
      }
      if (!cancelled) pollTimer = window.setTimeout(pollFirstBatch, 8000)
    }

    const boot = async () => {
      setLoading(true)
      try {
        let st = await refreshStatus()
        // 每次进入页面都幂等地触发一次启动检查；后端已有图片时会直接返回 ready，
        // 正在生成时由后端锁避免重复任务，避免前端只显示“加载中”但从未发起生成。
        if (!cancelled) setIsStarting(true)
        try {
          await phase2API.start()
          st = await refreshStatus()
        } catch (error) {
          console.error('启动或恢复第二阶段后台生成失败:', error)
        } finally {
          if (!cancelled) setIsStarting(false)
        }

        if (!(await loadFirstIncompleteBatch(st))) {
          pollTimer = window.setTimeout(pollFirstBatch, 5000)
        }
      } catch (error: any) {
        toast.error(error.response?.data?.detail || '加载第二阶段失败')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    boot()
    return () => {
      cancelled = true
      if (pollTimer !== undefined) window.clearTimeout(pollTimer)
    }
  }, [isAuthenticated, user, navigate, loadGroups, refreshStatus, editMode])

  const handlePreviousBatch = async () => {
    if (activeBatch <= 0) return
    const previousBatch = activeBatch - 1
    try {
      const st = await refreshStatus()
      if (!(st.ready_batches ?? []).includes(previousBatch)) {
        toast.error('上一组尚未准备好，请稍后再试')
        return
      }
      await loadGroups(previousBatch, true)
      setActiveBatch(previousBatch)
      toast.success('已返回上一组，可以重新评分')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || '返回上一组失败')
    }
  }

  const handleNextBatch = async () => {
    if (activeBatch >= totalBatches - 1) return
    const nextBatch = activeBatch + 1
    try {
      const st = await refreshStatus()
      if (!(st.ready_batches ?? []).includes(nextBatch)) {
        toast.error('下一组尚未准备好，请稍后再试')
        return
      }
      await loadGroups(nextBatch, true)
      setActiveBatch(nextBatch)
      toast.success('已进入下一组，可以重新评分')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || '进入下一组失败')
    }
  }

  const handleStart = async () => {
    setIsStarting(true)
    try {
      await phase2API.start()
      toast.success('已启动第二阶段生成，请稍候')
      const poll = async () => {
        const st = await refreshStatus()
        if (st.status === 'ready' || st.status === 'partial') {
          await loadGroups(0)
          setActiveBatch(0)
          return
        }
        if (st.job?.status === 'failed') {
          toast.error(st.job.error || st.job.last_error || '第二阶段图像生成失败，请查看页面提示')
          return
        }
        setTimeout(poll, 8000)
      }
      setTimeout(poll, 5000)
    } catch (error: any) {
      toast.error(error.response?.data?.detail || '启动失败')
    } finally {
      setIsStarting(false)
    }
  }

  const updateScore = (
    groupId: string,
    imageId: number,
    type: ScoreType,
    value: number,
  ) => {
    setGroupStates((prev) => {
      const gs = prev[groupId]
      if (!gs) return prev
      const cur = gs.scores[imageId] ?? {
        preference_score: null,
      }
      const updated = { ...cur }
      if (type === 'preference') updated.preference_score = value
      return {
        ...prev,
        [groupId]: { ...gs, scores: { ...gs.scores, [imageId]: updated } },
      }
    })
  }

  const handleBrokenImage = (
    _groupId: string,
    imageId: number
  ) => {
    // 保留失败图片，禁止通过删除图片绕过整组评分校验。
    setFailedImageIds(previous => {
      const next = new Set(previous)
      next.add(imageId)
      return next
    })
    setLoadedImageIds(previous => {
      const next = new Set(previous)
      next.delete(imageId)
      return next
    })
  }

  const validateGroup = (groupId: string, images: Phase2Image[]): Phase2RatingItem[] | null => {
    const gs = groupStates[groupId]
    if (!gs || gs.submitted) return null
    const items: Phase2RatingItem[] = []
    for (const img of images) {
      const s = gs.scores[img.id]

      if (!s || s.preference_score == null) {
        toast.error('请为本组所有图像填写个人风格偏好评分（1-10）')
        return null
      }

      items.push({
        image_id: img.id,
        preference_score: s.preference_score,
      })
    }

    if (!items.length) {
      toast.error('本组没有需要评分的图像')
      return null
    }
    return items
  }

  const handleSubmitGroup = async (group: Phase2Group) => {
    const gs = groupStates[group.group_id]
    if (!gs || gs.submitted || gs.submitting) return
    const imagesReady = group.images.every(image => loadedImageIds.has(image.id))
    if (!imagesReady) {
      toast.error('本组图片仍在加载，请等待图片全部显示后再提交')
      return
    }

    const items = validateGroup(group.group_id, group.images)
    if (!items) return

    setGroupStates((p) => ({
      ...p,
      [group.group_id]: { ...gs, submitting: true },
    }))
    try {
      await phase2API.submitRatings({
        group_id: group.group_id,
        group_batch: group.group_batch ?? activeBatch,
        ratings: items,
      })
      toast.success('本组已提交')
      setGroupStates((p) => ({
        ...p,
        [group.group_id]: { ...gs, submitted: true, submitting: false },
      }))

      const isLastBatch = activeBatch >= totalBatches - 1
      const allGroupsSubmitted = groups.every(
        current => current.group_id === group.group_id || Boolean(groupStates[current.group_id]?.submitted),
      )
      if (isLastBatch && allGroupsSubmitted) {
        navigate('/phase2/completion')
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || '提交失败')
      setGroupStates((p) => ({
        ...p,
        [group.group_id]: { ...gs, submitting: false },
      }))
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4" />
        <p className="text-lg text-gray-600">正在加载第二阶段...</p>
      </div>
    )
  }

  if (job?.status === 'failed' && groups.length === 0) {
    const failureReason = job.error || job.last_error || '第二阶段图像生成失败，暂无可评分图片。'
    return (
      <div className="max-w-2xl mx-auto mt-10 bg-white rounded-2xl shadow-lg p-8 border-2 border-red-200">
        <h1 className="text-2xl font-bold text-gray-900 mb-3">第二阶段图像生成失败</h1>
        <p className="text-gray-700 mb-4 leading-relaxed">
          第一阶段评分已达标，但第二阶段图片没有生成成功，因此暂时无法开始评分。
        </p>
        <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm leading-6 text-red-900">
          <div className="font-semibold mb-1">失败原因</div>
          <p className="break-words">{failureReason}</p>
        </div>
        <button
          type="button"
          onClick={handleStart}
          disabled={isStarting}
          className="w-full py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60"
        >
          {isStarting ? '正在重新启动...' : '重新尝试生成'}
        </button>
      </div>
    )
  }

  if (
    groups.length === 0 &&
    (status === 'pending' || status === 'partial' || job?.status === 'running')
  ) {
    const completed = job?.completed ?? 0
    const total = job?.total ?? 0
    const percent = total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0

    return (
      <div className="max-w-2xl mx-auto mt-10 bg-white rounded-2xl shadow-lg p-8 border-2 border-blue-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-100 border-t-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">第二阶段图像生成中</h1>
        </div>
        <p className="text-gray-700 mb-4 leading-relaxed">
          系统正在根据您在第一阶段的评分生成个性化目标图像，请耐心等待。
          生成完成后页面会自动显示可评分图片，请不要关闭页面或重复点击。
        </p>
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-4">
          <div className="flex justify-between text-sm font-medium text-blue-900 mb-2">
            <span>生成进度</span>
            <span>{completed} / {total || '-'} {total > 0 ? `（${percent}%）` : ''}</span>
          </div>
          <div className="h-3 rounded-full bg-blue-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-blue-600 transition-all duration-500"
              style={{ width: `${percent}%` }}
            />
          </div>
          {job?.stage && (
            <p className="mt-2 text-xs text-blue-700">当前状态：{job.stage}</p>
          )}
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
          <div className="font-semibold mb-1">请耐心等待</div>
          <p>图像生成可能需要几分钟。页面会自动刷新进度，不需要手动刷新或重复提交。</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto pb-16">
      <div className="mb-6 rounded-xl bg-indigo-50 border border-indigo-200 p-4">
        <h1 className="text-xl font-bold text-indigo-900">
          第二阶段评分 · 第 {activeBatch + 1} / {totalBatches} 组
        </h1>
        <p className="text-sm text-indigo-800 mt-1">
          请对所有图像在个人风格偏好分数上打分（1-10）。
          {editMode && <span className="block text-amber-700 mt-1">当前为修改模式，重新提交会覆盖原评分。</span>}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handlePreviousBatch}
            disabled={activeBatch <= 0}
            className="px-4 py-2 rounded-lg bg-white text-indigo-700 text-sm font-medium border border-indigo-300 hover:bg-indigo-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            返回上一组重新评分
          </button>
          {(
            <button
              type="button"
              onClick={handleNextBatch}
              disabled={activeBatch >= totalBatches - 1}
              className="px-4 py-2 rounded-lg bg-white text-indigo-700 text-sm font-medium border border-indigo-300 hover:bg-indigo-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              下一组
            </button>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {groups.map((group, groupIndex) => {
          const gs = groupStates[group.group_id]
          const expanded = gs?.expanded ?? false
          const submitted = gs?.submitted ?? false
          const imagesReady = group.images.every(image => loadedImageIds.has(image.id))
          return (
            <div key={group.group_id} className="bg-white rounded-2xl shadow border border-gray-200">
              <button
                type="button"
                className="w-full flex justify-between items-center px-5 py-4 border-b"
                onClick={() =>
                  setGroupStates((p) => ({
                    ...p,
                    [group.group_id]: { ...(p[group.group_id] as GroupLocalState), expanded: !expanded },
                  }))
                }
              >
                <span className="font-medium text-gray-900">
                  第 {groupIndex + 1} 个评分组
                </span>
                <span className="text-sm text-gray-500">
                  {submitted ? '已提交' : expanded ? '收起' : '展开'} · {group.images.length} 张
                </span>
              </button>
              {expanded && (
                <div className="p-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {group.images.map((img) => {
                      const sc = gs?.scores[img.id]
                      return (
                        <div
                          key={img.id}
                          className="rounded-xl border border-gray-200 overflow-hidden"
                        >
                          <div className="relative">
                            {failedImageIds.has(img.id) ? (
                              <div className="absolute inset-0 z-10 flex items-center justify-center bg-red-50 px-3 text-center text-sm text-red-600">
                                图片加载失败，请刷新页面后重试；加载成功前不能提交本组
                              </div>
                            ) : !loadedImageIds.has(img.id) ? (
                              <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-50 text-sm text-gray-500">
                                正在加载图片…
                              </div>
                            ) : null}
                            <img
                              src={img.image_url}
                              alt=""
                              className="w-full h-44 object-contain bg-gray-50 cursor-zoom-in"
                              title="点击放大查看"
                              onClick={() => {
                                if (loadedImageIds.has(img.id)) setPreviewImage(img)
                              }}
                              decoding="async"
                              onLoad={() => {
                                setLoadedImageIds(previous => {
                                  const next = new Set(previous)
                                  next.add(img.id)
                                  return next
                                })
                                setFailedImageIds(previous => {
                                  const next = new Set(previous)
                                  next.delete(img.id)
                                  return next
                                })
                              }}
                              onError={() =>
                                handleBrokenImage(
                                  group.group_id,
                                  img.id
                                )
                              }
                            />
                          </div>
                          {sc && loadedImageIds.has(img.id) && (
                            <div className="px-3 py-3">
                              <ScoreRow
                                label="个人风格偏好"
                                value={sc.preference_score}
                                disabled={submitted}
                                onChange={(value) =>
                                  updateScore(
                                    group.group_id,
                                    img.id,
                                    'preference',
                                    value
                                  )
                                }
                              />
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      disabled={submitted || gs?.submitting || !imagesReady}
                      onClick={() => handleSubmitGroup(group)}
                      className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
                    >
                      {submitted ? '已提交' : gs?.submitting ? '提交中...' : !imagesReady ? '图片加载中...' : '提交本组'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    {previewImage && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
        role="dialog"
        aria-modal="true"
        aria-label="放大图片预览"
        onClick={() => setPreviewImage(null)}
      >
        <button
          type="button"
          className="absolute right-4 top-4 rounded-full bg-white/90 px-4 py-2 text-sm font-medium text-gray-800 shadow hover:bg-white"
          onClick={() => setPreviewImage(null)}
        >
          关闭
        </button>
        <img
          src={previewImage.image_url}
          alt="放大预览"
          className="max-h-[90vh] max-w-[95vw] object-contain"
          onClick={(event) => event.stopPropagation()}
        />
      </div>
    )}
    </div>
  )
}

function ScoreRow({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string
  value: number | null
  onChange: (v: number) => void
  disabled?: boolean
}) {
  return (
    <div className="flex items-center justify-between gap-1">
      <span className="text-xs text-gray-600 w-14">{label}</span>
      <div className="flex flex-wrap gap-0.5">
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            disabled={disabled}
            onClick={() => onChange(n)}
            className={`w-6 h-6 text-[10px] rounded border ${
              value === n ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-300'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  )
}
